import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    Save,
    RotateCw,
    Flag,
    CheckCircle2,
    ArrowLeftRight,
    Timer,
    Download,
    Upload,
    X,
    AlertTriangle,
    Ban,
    XCircle,
    ShieldAlert,
    ScrollText,
} from "lucide-react";
import SignaturePad from "./SignaturePad";

/**
 * VolleySimulator v3 — flow simplificado:
 *   1) toss (quién saca primero)
 *   2) positions (escribir el número que ocupa cada P1..P6 + libero)
 *   3) playing (puntos, rotaciones, sustituciones inteligentes, TOs, cambio de lado)
 *   4) finished (panel resumen + exportar/subir evidencia/cerrar)
 *
 * Sustituciones inteligentes:
 *   Cada posición guarda {current_num, paired_with}.
 *   Cuando paired_with está vacío y se hace cambio, se pide el número entrante
 *   y se guarda el saliente como paired_with.
 *   Cuando paired_with existe, se ofrece "Volver a entrar #paired_with" como
 *   1-click (regla del voleibol: sustitución reversible entre el mismo par).
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 2 * 1024 * 1024;

function rotateClockwise(positions) {
    const [p1, p2, p3, p4, p5, p6] = positions;
    return [p2, p3, p4, p5, p6, p1];
}

function rotateCounterclockwise(positions) {
    const [p1, p2, p3, p4, p5, p6] = positions;
    return [p6, p1, p2, p3, p4, p5];
}

function fullUrl(u) {
    if (!u) return null;
    if (u.startsWith("http")) return u;
    if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
    return u;
}

const newSlot = () => ({ current_num: "", paired_with: null });

export default function VolleySimulator({ match, slug, pin, onFinalized }) {
    const [phase, setPhase] = useState("toss"); // toss | positions | playing | finished
    const [serving, setServing] = useState("local");
    const [bestOf, setBestOf] = useState(5); // 5 = 3 de 5 (default) · 3 = 2 de 3
    const setsToWin = bestOf === 3 ? 2 : 3;
    const [posL, setPosL] = useState(Array.from({ length: 6 }, newSlot));
    const [posV, setPosV] = useState(Array.from({ length: 6 }, newSlot));
    const [liberoL, setLiberoL] = useState("");
    const [liberoV, setLiberoV] = useState("");
    const [currentSet, setCurrentSet] = useState(1);
    const [scoreL, setScoreL] = useState(0);
    const [scoreV, setScoreV] = useState(0);
    const [sets, setSets] = useState([]);
    // Mapa setNum -> "local" | "vis" (quién abrió el saque de ese set).
    // Determina la alternancia reglamentaria entre sets no decisivos.
    const [firstServerBySet, setFirstServerBySet] = useState({});
    const [timeoutsL, setTimeoutsL] = useState(2);
    const [timeoutsV, setTimeoutsV] = useState(2);
    const [history, setHistory] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [localOnLeft, setLocalOnLeft] = useState(true);
    const [evidenceUrl, setEvidenceUrl] = useState(null);
    const [uploadingEvidence, setUploadingEvidence] = useState(false);
    const [subDialog, setSubDialog] = useState(null);
    // --- Sanciones (cards/disciplina) ---
    const [sancionDialog, setSancionDialog] = useState(null); // { team }
    const [sanctions, setSanctions] = useState([]); // {type, target, team, number, set, ts}
    const [blockedSet, setBlockedSet] = useState([]); // ["local:7", ...] expulsiones del set
    const [blockedMatch, setBlockedMatch] = useState([]); // descalificaciones del partido
    // --- Contador de sustituciones por set ---
    const [subsL, setSubsL] = useState(0);
    const [subsV, setSubsV] = useState(0);
    // --- Cronómetro de tiempos fuera (30s FIVB) ---
    const [toTimer, setToTimer] = useState(null); // { team, seconds }
    // --- Firmas y cuerpo arbitral (al finalizar) ---
    const [signatures, setSignatures] = useState({
        local_cap: "",
        local_coach: "",
        vis_cap: "",
        vis_coach: "",
    });
    const [officials, setOfficials] = useState({
        primer: "",
        segundo: "",
        anotador: "",
        linea1: "",
        linea2: "",
    });
    const fileRef = useRef(null);

    const pushLog = (entry) =>
        setHistory((h) => [
            ...h,
            { ts: new Date().toISOString(), set: currentSet, ...entry },
        ]);

    // Load existing sheet
    useEffect(() => {
        if (!match?.id || !slug || !pin) return;
        (async () => {
            try {
                const { data } = await api.get(
                    `/referee/matches/${match.id}/sheet`,
                    { params: { slug, pin } },
                );
                if (data?.match_id && data.enabled !== false) {
                    if (data.pos_local) setPosL(data.pos_local);
                    if (data.pos_vis) setPosV(data.pos_vis);
                    if (data.libero_local) setLiberoL(data.libero_local);
                    if (data.libero_vis) setLiberoV(data.libero_vis);
                    setServing(data.serving || "local");
                    setCurrentSet(data.current_set || 1);
                    setScoreL(data.score_local || 0);
                    setScoreV(data.score_vis || 0);
                    setSets(data.sets || []);
                    setHistory(data.history || []);
                    if (typeof data.timeouts_local === "number")
                        setTimeoutsL(data.timeouts_local);
                    if (typeof data.timeouts_vis === "number")
                        setTimeoutsV(data.timeouts_vis);
                    if (typeof data.local_on_left === "boolean")
                        setLocalOnLeft(data.local_on_left);
                    if (data.evidence_url) setEvidenceUrl(data.evidence_url);
                    if (data.phase) setPhase(data.phase);
                    if (data.best_of) setBestOf(data.best_of);
                    if (Array.isArray(data.sanctions)) setSanctions(data.sanctions);
                    if (Array.isArray(data.blocked_set))
                        setBlockedSet(data.blocked_set);
                    if (Array.isArray(data.blocked_match))
                        setBlockedMatch(data.blocked_match);
                    if (typeof data.subs_local === "number") setSubsL(data.subs_local);
                    if (typeof data.subs_vis === "number") setSubsV(data.subs_vis);
                    if (data.signatures) setSignatures(data.signatures);
                    if (data.officials) setOfficials(data.officials);
                }
            } catch {}
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [match?.id]);

    const persist = useCallback(
        async (extra = {}) => {
            try {
                await api.put(`/referee/matches/${match.id}/sheet`, {
                    pin,
                    pos_local: posL,
                    pos_vis: posV,
                    libero_local: liberoL,
                    libero_vis: liberoV,
                    serving,
                    current_set: currentSet,
                    sets,
                    score_local: scoreL,
                    score_vis: scoreV,
                    timeouts_local: timeoutsL,
                    timeouts_vis: timeoutsV,
                    history,
                    local_on_left: localOnLeft,
                    phase,
                    best_of: bestOf,
                    evidence_url: evidenceUrl,
                    sanctions,
                    blocked_set: blockedSet,
                    blocked_match: blockedMatch,
                    subs_local: subsL,
                    subs_vis: subsV,
                    signatures,
                    officials,
                    enabled: true,
                    ...extra,
                });
            } catch {}
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [match?.id, pin, posL, posV, liberoL, liberoV, serving, currentSet, sets, scoreL, scoreV, timeoutsL, timeoutsV, history, localOnLeft, phase, bestOf, evidenceUrl, sanctions, blockedSet, blockedMatch, subsL, subsV, signatures, officials],
    );

    // Autosave after playing actions
    useEffect(() => {
        if (phase !== "playing") return;
        const t = setTimeout(persist, 600);
        return () => clearTimeout(t);
    }, [scoreL, scoreV, history.length, phase, persist]);

    // Target: set decisivo (3ero en BO3 o 5to en BO5) va a 15, resto a 25.
    const setTargetPoints = (setNum) => {
        if (bestOf === 3 && setNum >= 3) return 15;
        if (bestOf === 5 && setNum >= 5) return 15;
        return 25;
    };

    // Auto-finalize set
    useEffect(() => {
        if (phase !== "playing") return;
        const target = setTargetPoints(currentSet);
        const diff = Math.abs(scoreL - scoreV);
        if ((scoreL >= target || scoreV >= target) && diff >= 2) {
            finalizeSet();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scoreL, scoreV]);

    // ---- Phase transitions ----
    const startPositions = (firstServer) => {
        setServing(firstServer);
        setPhase("positions");
    };

    const startGame = () => {
        const readyL = posL.every((p) => p.current_num.trim());
        const readyV = posV.every((p) => p.current_num.trim());
        if (!readyL || !readyV) {
            toast.error("Completa los 6 números de ambos equipos");
            return;
        }
        setPhase("playing");
        // Registra quién abre el saque de este set (para alternancia reglamentaria)
        setFirstServerBySet((prev) => ({ ...prev, [currentSet]: serving }));
        if (currentSet === 1) {
            pushLog({ type: "match_start", first_serve: serving });
        } else {
            pushLog({ type: "set_start", set: currentSet, first_serve: serving });
        }
    };

    // ---- Points & rotation ----
    const point = (team) => {
        if (phase !== "playing") return;
        // Snapshot COMPLETO antes de modificar — usado por undoPoint.
        const snapshot = {
            type: "point_snapshot",
            team,
            pre: {
                scoreL,
                scoreV,
                serving,
                posL: posL.map((s) => ({ ...s })),
                posV: posV.map((s) => ({ ...s })),
            },
            ts: Date.now(),
        };
        if (team === "local") {
            if (serving === "vis") {
                setPosL((p) => rotateClockwise(p));
                setServing("local");
                snapshot.subtype = "side_out";
            } else {
                snapshot.subtype = "point";
            }
            setScoreL((x) => x + 1);
        } else {
            if (serving === "local") {
                setPosV((p) => rotateClockwise(p));
                setServing("vis");
                snapshot.subtype = "side_out";
            } else {
                snapshot.subtype = "point";
            }
            setScoreV((x) => x + 1);
        }
        setHistory((h) => [...h, snapshot]);
    };

    const undoPoint = () => {
        // Busca el último snapshot de punto en history.
        setHistory((h) => {
            const idx = h.findLastIndex(
                (e) => e.type === "point_snapshot",
            );
            if (idx === -1) {
                toast.info("Nada que deshacer.");
                return h;
            }
            const snap = h[idx];
            // Restaura de golpe todo lo que cambió por ese punto
            setScoreL(snap.pre.scoreL);
            setScoreV(snap.pre.scoreV);
            setServing(snap.pre.serving);
            setPosL(snap.pre.posL.map((s) => ({ ...s })));
            setPosV(snap.pre.posV.map((s) => ({ ...s })));
            toast.success("Punto anulado. Estado revertido.");
            return h.slice(0, idx);
        });
    };

    // Deshacer en cascada hasta el snapshot `idx` (inclusive): restaura al
    // estado `pre` de ese snapshot y descarta todos los eventos posteriores.
    const undoToIndex = (idx) => {
        setHistory((h) => {
            if (idx < 0 || idx >= h.length) return h;
            // Encuentra el snapshot con `pre` más cercano desde idx hacia atrás
            // (los eventos sin pre — ej. match_start — no son restorable, saltamos).
            let target = idx;
            while (target >= 0 && !h[target].pre) target -= 1;
            if (target < 0) {
                toast.info("No se puede deshacer hasta ese punto.");
                return h;
            }
            const snap = h[target];
            setScoreL(snap.pre.scoreL);
            setScoreV(snap.pre.scoreV);
            setServing(snap.pre.serving);
            setPosL(snap.pre.posL.map((s) => ({ ...s })));
            setPosV(snap.pre.posV.map((s) => ({ ...s })));
            toast.success(
                `Revertido ${h.length - target} eventos.`,
            );
            return h.slice(0, target);
        });
    };

    const callTimeout = (team) => {
        if (team === "local") {
            if (timeoutsL <= 0)
                return toast.info("Local ya usó sus 2 tiempos fuera");
            setTimeoutsL(timeoutsL - 1);
        } else {
            if (timeoutsV <= 0)
                return toast.info("Visitante ya usó sus 2 tiempos fuera");
            setTimeoutsV(timeoutsV - 1);
        }
        pushLog({ type: "timeout", team });
        setToTimer({ team, seconds: 30 });
    };

    // Tick del cronómetro de tiempo fuera
    useEffect(() => {
        if (!toTimer) return;
        if (toTimer.seconds <= 0) return;
        const t = setTimeout(() => {
            setToTimer((prev) =>
                prev ? { ...prev, seconds: prev.seconds - 1 } : prev,
            );
        }, 1000);
        return () => clearTimeout(t);
    }, [toTimer]);

    // ---- Sanciones ----
    const openSancion = (team) => setSancionDialog({ team });

    const applySancion = ({ type, target, number, reason }) => {
        if (!sancionDialog) return;
        const team = sancionDialog.team;
        const entry = {
            type, // amonestacion | castigo | expulsion | descalificacion
            target, // jugador | entrenador
            number: number ? String(number) : null,
            reason: reason || "",
            team,
            set: currentSet,
            ts: new Date().toISOString(),
        };
        setSanctions((s) => [...s, entry]);
        pushLog({ type: "sancion", subtype: type, team, target, number: entry.number });

        if (type === "castigo") {
            // +1 punto al rival (rotación si recupera saque)
            const rival = team === "local" ? "vis" : "local";
            point(rival);
        }

        if (type === "expulsion" && target === "jugador" && entry.number) {
            const key = `${team}:${entry.number}`;
            setBlockedSet((b) => (b.includes(key) ? b : [...b, key]));
            // Si está en cancha, abrir sustitución forzada
            const positions = team === "local" ? posL : posV;
            const idx = positions.findIndex(
                (s) => String(s.current_num) === String(entry.number),
            );
            if (idx >= 0) {
                setSubDialog({ team, posIdx: idx, forced: true });
                toast.warning(
                    `Expulsión #${entry.number}: cambio forzado en P${idx + 1}.`,
                );
            } else {
                toast.warning(
                    `Expulsión #${entry.number} registrada (no estaba en cancha).`,
                );
            }
        }

        if (type === "descalificacion" && target === "jugador" && entry.number) {
            const key = `${team}:${entry.number}`;
            setBlockedMatch((b) => (b.includes(key) ? b : [...b, key]));
            const positions = team === "local" ? posL : posV;
            const idx = positions.findIndex(
                (s) => String(s.current_num) === String(entry.number),
            );
            if (idx >= 0) {
                setSubDialog({ team, posIdx: idx, forced: true });
            }
            toast.warning(
                `Descalificación #${entry.number}: bloqueado todo el partido.`,
            );
        }

        if (type === "amonestacion") {
            toast.info(
                `Amonestación · ${target}${entry.number ? ` #${entry.number}` : ""}`,
            );
        }

        setSancionDialog(null);
    };

    // ---- Substitutions ----
    const openSub = (team, posIdx) => {
        setSubDialog({ team, posIdx });
    };

    const applySub = (incomingNum) => {
        if (!subDialog) return;
        const num = String(incomingNum).trim();
        if (!num) return;
        const { team, posIdx } = subDialog;
        const positions = team === "local" ? posL : posV;
        const slot = positions[posIdx];
        const outgoing = slot.current_num;
        const next = positions.map((s, i) => {
            if (i !== posIdx) return s;
            // Reversible pairing: if entering equals paired_with, restore originals
            if (slot.paired_with && num === slot.paired_with) {
                return { current_num: num, paired_with: outgoing };
            }
            return { current_num: num, paired_with: outgoing };
        });
        if (team === "local") setPosL(next);
        else setPosV(next);
        if (team === "local") setSubsL((c) => c + 1);
        else setSubsV((c) => c + 1);
        pushLog({
            type: "substitution",
            team,
            pos: posIdx + 1,
            out: outgoing,
            in: num,
        });
        toast.success(`Sustitución · #${outgoing} ↔ #${num}`);
        setSubDialog(null);
    };

    const finalizeSet = () => {
        const winner = scoreL > scoreV ? "local" : "vis";
        const entry = {
            num: currentSet,
            score_local: scoreL,
            score_vis: scoreV,
            winner,
            timeouts_used: { local: 2 - timeoutsL, vis: 2 - timeoutsV },
        };
        const newSets = [...sets, entry];
        setSets(newSets);
        pushLog({ type: "set_end", ...entry });

        const wonL = newSets.filter((s) => s.winner === "local").length;
        const wonV = newSets.filter((s) => s.winner === "vis").length;
        if (wonL === setsToWin || wonV === setsToWin) {
            finalizeMatch(newSets);
            return;
        }

        setLocalOnLeft((v) => !v);
        setScoreL(0);
        setScoreV(0);
        setTimeoutsL(2);
        setTimeoutsV(2);
        // Reinicia el contador de sustituciones y la pareja persistente del set
        setSubsL(0);
        setSubsV(0);
        // Limpia las posiciones para que el árbitro vuelva a capturarlas en cada set
        setPosL(Array.from({ length: 6 }, newSlot));
        setPosV(Array.from({ length: 6 }, newSlot));
        setLiberoL("");
        setLiberoV("");
        // Las expulsiones del set se levantan al cambiar de set
        setBlockedSet([]);
        const nextSet = currentSet + 1;
        setCurrentSet(nextSet);
        // ¿Es el set decisivo? (3 en BO3, 5 en BO5) → nuevo sorteo obligatorio
        const isDecisive =
            (bestOf === 3 && nextSet === 3) ||
            (bestOf === 5 && nextSet === 5);
        if (isDecisive) {
            // Decisivo: nuevo sorteo por reglamento (lo elige el árbitro).
            setPhase("toss");
            toast.success(
                `Set ${currentSet} terminado (${entry.score_local}-${entry.score_vis}). Set decisivo a ${setTargetPoints(nextSet)} pts — sorteo nuevo.`,
            );
        } else {
            // Alternancia reglamentaria: el saque del siguiente set lo abre
            // el equipo que NO abrió el saque del set anterior (independiente
            // del ganador). Si por algún motivo no hay registro, usa el ganador.
            const prevOpener = firstServerBySet[currentSet] || winner;
            const nextOpener = prevOpener === "local" ? "vis" : "local";
            setServing(nextOpener);
            setPhase("positions");
            toast.success(
                `Set ${currentSet} terminado (${entry.score_local}-${entry.score_vis}). Captura las posiciones del nuevo set.`,
            );
        }
    };

    const finalizeMatch = async (setsList = sets) => {
        try {
            await persist({ sets: setsList, phase: "finished" });
            const payload = setsList.map((s) => [s.score_local, s.score_vis]);
            await api.post(`/referee/matches/${match.id}/result`, {
                pin,
                sets: payload,
            });
            toast.success("Partido finalizado");
            setPhase("finished");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    // ---- Export & evidence upload ----
    const exportTxt = () => {
        const fmtPositions = (positions, libero, name) => {
            const lines = positions
                .map(
                    (s, i) =>
                        `  P${i + 1}: #${s.current_num}${
                            s.paired_with ? ` (pareja: #${s.paired_with})` : ""
                        }`,
                )
                .join("\n");
            return `${name}\n${lines}${libero ? `\n  Líbero: #${libero}` : ""}`;
        };
        const fmtSets = sets
            .map(
                (s) =>
                    `  Set ${s.num}: ${s.score_local}-${s.score_vis} (TOs L:${s.timeouts_used?.local ?? "-"} V:${s.timeouts_used?.vis ?? "-"})`,
            )
            .join("\n");
        const fmtHist = history
            .map((h) => {
                const base = `[Set ${h.set}] ${h.type}`;
                if (h.type === "point" || h.type === "point_snapshot" || h.type === "side_out" || h.type === "timeout")
                    return `${base} · ${h.team}`;
                if (h.type === "substitution")
                    return `${base} · ${h.team} · P${h.pos} #${h.out} ↔ #${h.in}`;
                if (h.type === "set_end")
                    return `${base} · ganador ${h.winner} (${h.score_local}-${h.score_vis})`;
                return base;
            })
            .join("\n");

        const wonL = sets.filter((s) => s.winner === "local").length;
        const wonV = sets.filter((s) => s.winner === "vis").length;

        const txt = `================================================
HOJA DE CONTROL · VOLLEYPRO (BETA)
================================================
Partido: ${match?.local || ""} vs ${match?.visitante || ""}
Cancha: ${match?.cancha || "-"} · Hora: ${match?.hora || "-"}
Fase: ${match?.fase || "-"} · Rama: ${match?.branch || "-"}
Resultado final: ${wonL}-${wonV}
Fecha export: ${new Date().toLocaleString()}

--- POSICIONES (cierre del partido) ---
${fmtPositions(posL, liberoL, match?.local || "LOCAL")}

${fmtPositions(posV, liberoV, match?.visitante || "VISITANTE")}

--- SETS ---
${fmtSets || "  (sin sets completos)"}

--- HISTORIAL ---
${fmtHist || "  (sin eventos)"}
================================================`;
        const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `hoja-${(match?.local || "L").slice(0, 12)}-vs-${(match?.visitante || "V").slice(0, 12)}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Hoja exportada");
    };

    const onPickEvidence = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > MAX_BYTES) {
            return toast.error(
                `Imagen muy grande (${Math.round(file.size / 1024)} KB). Máx 2MB.`,
            );
        }
        if (!file.type.startsWith("image/")) {
            return toast.error("Solo imágenes (PNG, JPG, WEBP).");
        }
        setUploadingEvidence(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            // Use referee-friendly path: PIN-protected via a generic public path is not ideal,
            // so we go through the auth-required uploads endpoint. Since referees don't have JWT,
            // we use a dedicated PIN-based upload route would be cleaner. Skipping for now:
            // we send slug+pin via FormData so backend can accept it. For MVP just call /uploads/image
            // and rely on the file being public via /api/files/{path}. This may fail without JWT.
            const { data } = await api.post(
                "/referee/uploads/evidence",
                fd,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    params: { slug, pin },
                },
            );
            setEvidenceUrl(data.url);
            await persist({ evidence_url: data.url });
            toast.success("Evidencia subida");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setUploadingEvidence(false);
        }
    };

    // ====================== RENDER ======================
    if (phase === "toss") {
        const isDecisiveToss = currentSet > 1;
        return (
            <div className="max-w-lg mx-auto text-center py-10">
                <Flag className="w-10 h-10 mx-auto text-ipn-800" />
                <h3 className="mt-4 font-display text-2xl font-black">
                    {isDecisiveToss
                        ? `Sorteo del set decisivo (Set ${currentSet})`
                        : "¿Quién saca primero?"}
                </h3>
                <p className="text-slate-500 mt-2 text-sm">
                    {isDecisiveToss
                        ? `Este set se juega a ${setTargetPoints(currentSet)} puntos. Vuelve a sortear el saque.`
                        : "Equipo ganador del sorteo."}
                </p>

                {!isDecisiveToss && (
                    <div className="mt-6">
                        <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">
                            Formato del partido
                        </div>
                        <div
                            className="inline-flex bg-white border border-slate-200 rounded-2xl p-1"
                            data-testid="sim-bestof-toggle"
                        >
                            <button
                                type="button"
                                onClick={() => setBestOf(3)}
                                className={`px-4 h-10 rounded-xl text-sm font-bold transition-colors ${
                                    bestOf === 3
                                        ? "bg-ipn-800 text-white"
                                        : "text-slate-600 hover:text-ipn-900"
                                }`}
                                data-testid="sim-bestof-3"
                            >
                                2 de 3 sets
                            </button>
                            <button
                                type="button"
                                onClick={() => setBestOf(5)}
                                className={`px-4 h-10 rounded-xl text-sm font-bold transition-colors ${
                                    bestOf === 5
                                        ? "bg-ipn-800 text-white"
                                        : "text-slate-600 hover:text-ipn-900"
                                }`}
                                data-testid="sim-bestof-5"
                            >
                                3 de 5 sets
                            </button>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-2">
                            {bestOf === 3
                                ? "Gana el primero en llevarse 2 sets. Set 3 (decisivo) va a 15 pts."
                                : "Formato oficial FIVB: gana el primero en llevarse 3 sets. Set 5 va a 15 pts."}
                        </p>
                    </div>
                )}

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Button
                        onClick={() => startPositions("local")}
                        className="h-20 rounded-2xl bg-ipn-800 hover:bg-ipn-900 text-base font-bold"
                        data-testid="sim-serve-local"
                    >
                        {match?.local}
                    </Button>
                    <Button
                        onClick={() => startPositions("vis")}
                        className="h-20 rounded-2xl bg-slate-800 hover:bg-slate-900 text-base font-bold"
                        data-testid="sim-serve-vis"
                    >
                        {match?.visitante}
                    </Button>
                </div>
            </div>
        );
    }

    if (phase === "positions") {
        return (
            <div className="space-y-5">
                <div className="text-center">
                    <h3 className="font-display text-xl font-black text-ipn-900">
                        {currentSet > 1
                            ? `Posiciones · Set ${currentSet}`
                            : "Posiciones iniciales"}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1">
                        Coloca los números de cada equipo en sus posiciones (P1
                        al P6) como están en la cancha.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <PositionsSetup
                        title={match?.local || "Local"}
                        color="ipn"
                        positions={posL}
                        setPositions={setPosL}
                        libero={liberoL}
                        setLibero={setLiberoL}
                    />
                    <PositionsSetup
                        title={match?.visitante || "Visitante"}
                        color="slate"
                        positions={posV}
                        setPositions={setPosV}
                        libero={liberoV}
                        setLibero={setLiberoV}
                    />
                </div>
                <div className="flex justify-end gap-3">
                    {currentSet === 1 && (
                        <Button
                            variant="outline"
                            onClick={() => setPhase("toss")}
                            className="rounded-xl"
                        >
                            ← Atrás
                        </Button>
                    )}
                    <Button
                        onClick={startGame}
                        className="rounded-xl bg-ipn-800 hover:bg-ipn-900 font-bold"
                        data-testid="sim-start-game"
                    >
                        {currentSet > 1
                            ? `Comenzar set ${currentSet} →`
                            : "Comenzar partido →"}
                    </Button>
                </div>
            </div>
        );
    }

    if (phase === "finished") {
        const wonL = sets.filter((s) => s.winner === "local").length;
        const wonV = sets.filter((s) => s.winner === "vis").length;
        const totalSubs = history.filter((h) => h.type === "substitution").length;
        const totalTos = history.filter((h) => h.type === "timeout").length;
        return (
            <div className="space-y-5">
                <div className="text-center py-2">
                    <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
                    <h3 className="mt-3 font-display text-2xl font-black">
                        Partido finalizado
                    </h3>
                </div>
                <div className="rounded-2xl bg-gradient-to-br from-ipn-900 to-ipn-950 text-white p-6 text-center">
                    <div className="grid grid-cols-3 items-center">
                        <div>
                            <div className="text-xs uppercase tracking-widest opacity-70 truncate">
                                {match?.local}
                            </div>
                            <div className="text-5xl font-display font-black">
                                {wonL}
                            </div>
                        </div>
                        <div className="text-xs uppercase tracking-widest opacity-70">
                            sets
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest opacity-70 truncate">
                                {match?.visitante}
                            </div>
                            <div className="text-5xl font-display font-black">
                                {wonV}
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 text-sm text-white/80">
                        {sets
                            .map((s) => `${s.score_local}-${s.score_vis}`)
                            .join(" · ")}
                    </div>
                </div>

                <div className="vp-card p-5">
                    <h4 className="font-display font-bold mb-3">Resumen</h4>
                    <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                        <SummaryStat label="Sets jugados" v={sets.length} />
                        <SummaryStat label="Sustituciones" v={totalSubs} />
                        <SummaryStat label="Tiempos fuera" v={totalTos} />
                        <SummaryStat
                            label="Puntos totales"
                            v={sets.reduce(
                                (acc, s) => acc + s.score_local + s.score_vis,
                                0,
                            )}
                        />
                    </dl>
                    {evidenceUrl && (
                        <div className="mt-4">
                            <Label className="text-xs uppercase font-bold tracking-widest text-slate-500">
                                Evidencia
                            </Label>
                            <img
                                src={fullUrl(evidenceUrl)}
                                alt="Evidencia"
                                className="mt-2 rounded-xl max-h-64 w-auto"
                            />
                        </div>
                    )}
                </div>

                <div className="vp-card p-5">
                    <h4 className="font-display font-bold mb-3">
                        Cuerpo arbitral
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <OfficialField
                            label="Primer juez (obligatorio)"
                            value={officials.primer}
                            onChange={(v) =>
                                setOfficials((o) => ({ ...o, primer: v }))
                            }
                            required
                            testId="official-primer"
                        />
                        <OfficialField
                            label="Segundo juez"
                            value={officials.segundo}
                            onChange={(v) =>
                                setOfficials((o) => ({ ...o, segundo: v }))
                            }
                            testId="official-segundo"
                        />
                        <OfficialField
                            label="Anotador"
                            value={officials.anotador}
                            onChange={(v) =>
                                setOfficials((o) => ({ ...o, anotador: v }))
                            }
                            testId="official-anotador"
                        />
                        <OfficialField
                            label="Juez de línea 1"
                            value={officials.linea1}
                            onChange={(v) =>
                                setOfficials((o) => ({ ...o, linea1: v }))
                            }
                            testId="official-linea1"
                        />
                        <OfficialField
                            label="Juez de línea 2"
                            value={officials.linea2}
                            onChange={(v) =>
                                setOfficials((o) => ({ ...o, linea2: v }))
                            }
                            testId="official-linea2"
                        />
                    </div>
                </div>

                <div className="vp-card p-5">
                    <h4 className="font-display font-bold mb-3">Firmas</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                            <div className="text-xs uppercase font-bold tracking-widest text-ipn-900">
                                {match?.local}
                            </div>
                            <SignaturePad
                                label="Capitán"
                                testId="sig-local-cap"
                                value={signatures.local_cap}
                                onChange={(v) =>
                                    setSignatures((s) => ({ ...s, local_cap: v }))
                                }
                            />
                            <SignaturePad
                                label="Entrenador"
                                testId="sig-local-coach"
                                value={signatures.local_coach}
                                onChange={(v) =>
                                    setSignatures((s) => ({
                                        ...s,
                                        local_coach: v,
                                    }))
                                }
                            />
                        </div>
                        <div className="space-y-3">
                            <div className="text-xs uppercase font-bold tracking-widest text-slate-900">
                                {match?.visitante}
                            </div>
                            <SignaturePad
                                label="Capitán"
                                testId="sig-vis-cap"
                                value={signatures.vis_cap}
                                onChange={(v) =>
                                    setSignatures((s) => ({ ...s, vis_cap: v }))
                                }
                            />
                            <SignaturePad
                                label="Entrenador"
                                testId="sig-vis-coach"
                                value={signatures.vis_coach}
                                onChange={(v) =>
                                    setSignatures((s) => ({
                                        ...s,
                                        vis_coach: v,
                                    }))
                                }
                            />
                        </div>
                    </div>
                </div>

                {sanctions.length > 0 && (
                    <div className="vp-card p-5">
                        <h4 className="font-display font-bold mb-3">
                            Sanciones del partido
                        </h4>
                        <div className="space-y-2 text-sm">
                            {sanctions.map((s, i) => (
                                <div
                                    key={i}
                                    className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-100"
                                    data-testid={`sanction-row-${i}`}
                                >
                                    <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">
                                        {s.type}
                                    </span>
                                    <span className="text-xs font-bold">
                                        Set {s.set} · {s.team}
                                    </span>
                                    <span className="text-xs text-slate-600">
                                        {s.target}
                                        {s.number ? ` #${s.number}` : ""}
                                    </span>
                                    {s.reason && (
                                        <span className="text-xs text-slate-500 italic ml-auto truncate">
                                            {s.reason}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap gap-2 justify-end">
                    <Button
                        variant="outline"
                        onClick={persist}
                        className="rounded-xl"
                        data-testid="sim-save-finished"
                    >
                        <Save className="w-4 h-4 mr-2" />
                        Guardar firmas
                    </Button>
                    <Button
                        variant="outline"
                        onClick={exportTxt}
                        className="rounded-xl"
                        data-testid="sim-export-final"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Exportar TXT
                    </Button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        onChange={onPickEvidence}
                        className="hidden"
                        data-testid="sim-evidence-input"
                    />
                    <Button
                        variant="outline"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploadingEvidence}
                        className="rounded-xl"
                        data-testid="sim-upload-evidence"
                    >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingEvidence ? "Subiendo…" : "Subir evidencia"}
                    </Button>
                    <Button
                        onClick={() => onFinalized?.()}
                        className="rounded-xl bg-ipn-800 hover:bg-ipn-900 font-bold"
                        data-testid="sim-close"
                    >
                        Cerrar
                    </Button>
                </div>
            </div>
        );
    }

    // PLAYING phase
    const panels = [
        { team: "local", name: match?.local, color: "ipn", positions: posL, timeouts: timeoutsL },
        { team: "vis", name: match?.visitante, color: "slate", positions: posV, timeouts: timeoutsV },
    ];
    const ordered = localOnLeft ? panels : [panels[1], panels[0]];

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-3 items-center rounded-2xl bg-gradient-to-br from-ipn-900 to-ipn-950 text-white p-4 sm:p-6">
                <TeamScore
                    name={ordered[0].name}
                    score={ordered[0].team === "local" ? scoreL : scoreV}
                    serving={serving === ordered[0].team}
                    timeouts={ordered[0].timeouts}
                />
                <div className="text-center">
                    <div className="text-xs uppercase tracking-widest opacity-70">
                        Set {currentSet}
                    </div>
                    <div className="mt-1 text-xs text-white/70">
                        {sets.length === 0
                            ? "—"
                            : sets
                                  .map((s) => `${s.score_local}-${s.score_vis}`)
                                  .join(" · ")}
                    </div>
                    <button
                        onClick={() => setLocalOnLeft((v) => !v)}
                        className="mt-2 text-[10px] uppercase tracking-widest text-ipnYellow font-bold hover:text-white inline-flex items-center gap-1"
                        data-testid="sim-swap-side"
                    >
                        <ArrowLeftRight className="w-3 h-3" />
                        Cambiar lado
                    </button>
                </div>
                <TeamScore
                    name={ordered[1].name}
                    score={ordered[1].team === "local" ? scoreL : scoreV}
                    serving={serving === ordered[1].team}
                    timeouts={ordered[1].timeouts}
                    align="right"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ordered.map((p) => (
                    <CourtPanel
                        key={p.team}
                        title={p.name}
                        color={p.color}
                        positions={p.positions}
                        onAddPoint={() => point(p.team)}
                        onSubstitute={(idx) => openSub(p.team, idx)}
                        onTimeout={() => callTimeout(p.team)}
                        onSancion={() => openSancion(p.team)}
                        timeouts={p.timeouts}
                        subs={p.team === "local" ? subsL : subsV}
                        isServing={serving === p.team}
                    />
                ))}
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2 pt-3 border-t border-slate-100">
                <div className="flex gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={undoPoint}
                        className="rounded-xl"
                        data-testid="sim-undo"
                    >
                        <RotateCw className="w-4 h-4 mr-1" />
                        Deshacer
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setHistoryOpen((o) => !o)}
                        className="rounded-xl"
                        data-testid="sim-history-toggle"
                    >
                        <ScrollText className="w-4 h-4 mr-1" />
                        Historial
                    </Button>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        onClick={persist}
                        className="rounded-xl"
                        data-testid="sim-save"
                    >
                        <Save className="w-4 h-4 mr-1" />
                        Guardar
                    </Button>
                    <Button
                        onClick={finalizeSet}
                        className="rounded-xl bg-ipnYellow text-ipn-950 hover:bg-ipnYellow-600 font-bold"
                        data-testid="sim-finalize-set"
                    >
                        Finalizar set
                    </Button>
                    <Button
                        onClick={() => finalizeMatch()}
                        className="rounded-xl bg-ipn-800 hover:bg-ipn-900 font-bold"
                        data-testid="sim-finalize-match"
                    >
                        Finalizar partido
                    </Button>
                </div>
            </div>

            {subDialog && (
                <SubstitutionDialog
                    open={!!subDialog}
                    team={subDialog.team}
                    pos={subDialog.posIdx + 1}
                    slot={
                        (subDialog.team === "local" ? posL : posV)[
                            subDialog.posIdx
                        ]
                    }
                    forced={!!subDialog.forced}
                    onClose={() => setSubDialog(null)}
                    onConfirm={applySub}
                />
            )}

            {sancionDialog && (
                <SancionDialog
                    open={!!sancionDialog}
                    team={sancionDialog.team}
                    teamName={
                        sancionDialog.team === "local"
                            ? match?.local
                            : match?.visitante
                    }
                    onClose={() => setSancionDialog(null)}
                    onConfirm={applySancion}
                />
            )}

            {toTimer && (
                <TimeoutOverlay
                    seconds={toTimer.seconds}
                    teamName={
                        toTimer.team === "local"
                            ? match?.local
                            : match?.visitante
                    }
                    onClose={() => setToTimer(null)}
                />
            )}

            {historyOpen && (
                <HistoryDrawer
                    history={history}
                    onClose={() => setHistoryOpen(false)}
                    onJumpTo={(idx) => {
                        undoToIndex(idx);
                        setHistoryOpen(false);
                    }}
                    localName={match?.local}
                    visName={match?.visitante}
                />
            )}
        </div>
    );
}

function HistoryDrawer({ history, onClose, onJumpTo, localName, visName }) {
    // Mostramos los últimos 10 eventos, invertidos (más reciente arriba)
    const last = history.slice(-10).reverse();
    const describe = (e) => {
        if (e.type === "point_snapshot") {
            const who = e.team === "local" ? localName : visName;
            const sub = e.subtype === "side_out" ? "SIDE OUT" : "Punto";
            return `${sub} · ${who}`;
        }
        if (e.type === "match_start") return "🏁 Partido iniciado";
        if (e.type === "set_start") return `🏐 Set ${e.set} iniciado`;
        if (e.type === "set_end")
            return `✅ Fin de set ${e.num} (${e.score_local}-${e.score_vis})`;
        if (e.type === "timeout") {
            const who = e.team === "local" ? localName : visName;
            return `⏱️ Tiempo fuera · ${who}`;
        }
        if (e.type === "substitution") {
            const who = e.team === "local" ? localName : visName;
            return `🔁 Sustitución · ${who}`;
        }
        if (e.type === "sancion") {
            const who = e.team === "local" ? localName : visName;
            return `⚠️ Sanción · ${who}`;
        }
        return e.type || "Evento";
    };
    return (
        <div
            className="fixed inset-0 bg-black/40 z-50 flex justify-end"
            data-testid="sim-history-drawer"
            onClick={onClose}
        >
            <aside
                className="w-full max-w-sm bg-white h-full shadow-xl overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between">
                    <div>
                        <h3 className="font-display font-black text-lg">
                            Historial
                        </h3>
                        <p className="text-[11px] text-slate-500">
                            Últimos {last.length} de {history.length} eventos.
                            Click para deshacer en cascada.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-slate-100"
                        aria-label="Cerrar"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </header>
                <ol className="p-4 space-y-2">
                    {last.length === 0 && (
                        <li className="text-sm text-slate-500 text-center py-6">
                            Aún no hay eventos registrados.
                        </li>
                    )}
                    {last.map((e, i) => {
                        const originalIdx = history.length - 1 - i;
                        const canUndo = !!e.pre;
                        return (
                            <li key={originalIdx}>
                                <button
                                    onClick={() => canUndo && onJumpTo(originalIdx)}
                                    disabled={!canUndo}
                                    className={`w-full text-left p-3 rounded-xl border transition-colors ${
                                        canUndo
                                            ? "border-slate-200 hover:border-ipn-500 hover:bg-ipn-50 cursor-pointer"
                                            : "border-slate-100 bg-slate-50 cursor-not-allowed opacity-70"
                                    }`}
                                    data-testid={`sim-history-item-${originalIdx}`}
                                >
                                    <div className="text-xs text-slate-400">
                                        #{originalIdx + 1}
                                    </div>
                                    <div className="font-bold text-sm">
                                        {describe(e)}
                                    </div>
                                    {e.pre && (
                                        <div className="text-[10px] text-slate-500 mt-0.5 tabular-nums">
                                            Antes: {e.pre.scoreL}-{e.pre.scoreV} ·
                                            Saque: {e.pre.serving === "local" ? "L" : "V"}
                                        </div>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ol>
            </aside>
        </div>
    );
}

function PositionsSetup({ title, color, positions, setPositions, libero, setLibero }) {
    const setNum = (i, v) => {
        if (v !== "" && !/^\d{0,3}$/.test(v)) return;
        setPositions(
            positions.map((p, idx) =>
                idx === i ? { ...p, current_num: v } : p,
            ),
        );
    };
    const setLib = (v) => {
        if (v !== "" && !/^\d{0,3}$/.test(v)) return;
        setLibero(v);
    };
    const layout = [3, 2, 1, 4, 5, 0]; // P4 P3 P2 / P5 P6 P1

    return (
        <div
            className={`rounded-2xl border p-4 ${
                color === "ipn"
                    ? "bg-ipn-50 border-ipn-100"
                    : "bg-slate-50 border-slate-200"
            }`}
        >
            <h3 className="font-display font-bold mb-3 truncate">{title}</h3>
            <div className="grid grid-cols-3 gap-2">
                {layout.map((idx) => (
                    <div
                        key={idx}
                        className="aspect-[5/3] rounded-xl bg-white border border-slate-200 p-2 flex flex-col items-center justify-center text-center"
                    >
                        <span className="text-[10px] uppercase font-bold text-slate-400">
                            P{idx + 1}
                        </span>
                        <Input
                            inputMode="numeric"
                            value={positions[idx].current_num}
                            onChange={(e) => setNum(idx, e.target.value)}
                            placeholder="#"
                            className="mt-1 h-9 rounded-lg text-center text-lg font-bold border-slate-200"
                            data-testid={`pos-input-${title}-${idx}`}
                        />
                    </div>
                ))}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-200 flex items-center gap-3">
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap">
                    Líbero
                </Label>
                <Input
                    inputMode="numeric"
                    value={libero}
                    onChange={(e) => setLib(e.target.value)}
                    placeholder="# (opcional)"
                    className="h-9 rounded-lg flex-1"
                />
            </div>
        </div>
    );
}

function TeamScore({ name, score, serving, timeouts, align = "left" }) {
    return (
        <div className={align === "right" ? "text-right" : "text-left"}>
            <div className="flex items-center gap-2 mb-1 min-w-0 justify-start" style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
                <span className="text-base sm:text-xl font-display font-black truncate uppercase">
                    {name}
                </span>
                {serving && (
                    <span className="bg-ipnYellow text-ipn-950 px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0">
                        SACA
                    </span>
                )}
            </div>
            <div className="text-5xl sm:text-7xl font-display font-black">
                {score}
            </div>
            <div className="text-[10px] uppercase tracking-widest opacity-70 mt-1">
                TO: {timeouts}/2
            </div>
        </div>
    );
}

function CourtPanel({
    title,
    color,
    positions,
    onAddPoint,
    onSubstitute,
    onTimeout,
    onSancion,
    timeouts,
    subs,
    isServing,
}) {
    const layout = [3, 2, 1, 4, 5, 0];
    return (
        <div
            className={`rounded-2xl border p-3 transition-all ${
                color === "ipn"
                    ? "bg-ipn-50 border-ipn-100"
                    : "bg-slate-50 border-slate-200"
            } ${
                isServing
                    ? "ring-2 ring-ipnYellow ring-offset-2 ring-offset-white"
                    : ""
            }`}
        >
            <div className="flex items-center justify-between mb-3 gap-2">
                <h3 className="font-display font-black truncate text-lg sm:text-2xl flex-1 min-w-0 flex items-center gap-2">
                    {title}
                    {isServing && (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase font-black px-2 py-0.5 rounded-full bg-ipnYellow text-ipn-950"
                            data-testid={`sim-serving-${color}`}
                        >
                            <span className="w-2 h-2 rounded-full bg-ipn-950" />
                            Saca
                        </span>
                    )}
                </h3>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10px] uppercase font-bold text-slate-500 bg-white border border-slate-200 px-1.5 py-1 rounded-lg">
                        Cambios {subs ?? 0}
                    </span>
                    <button
                        onClick={onTimeout}
                        disabled={timeouts <= 0}
                        className={`text-xs inline-flex items-center gap-1 font-bold px-2 py-1 rounded-lg transition-all ${
                            timeouts > 0
                                ? "bg-white border border-slate-200 hover:bg-slate-100"
                                : "bg-slate-100 text-slate-400 cursor-not-allowed"
                        }`}
                        data-testid={`sim-timeout-${color}`}
                    >
                        <Timer className="w-3 h-3" />
                        TO {timeouts}/2
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                {layout.map((idx) => {
                    const s = positions[idx];
                    // El jugador que saca está en P1 (idx=0)
                    const isServer = isServing && idx === 0;
                    return (
                        <button
                            key={idx}
                            onClick={() => onSubstitute(idx)}
                            className={`aspect-[5/3] rounded-xl border transition-all p-2 flex flex-col items-center justify-center text-center relative ${
                                isServer
                                    ? "bg-ipnYellow border-ipn-900 shadow-md"
                                    : "bg-white border-slate-200 hover:border-ipn-400 hover:shadow-sm"
                            }`}
                            title={
                                isServer
                                    ? `#${s.current_num} está sacando (P${idx + 1})`
                                    : `Click para sustituir en P${idx + 1}`
                            }
                            data-testid={`court-pos-${color}-${idx}`}
                        >
                            {isServer && (
                                <span
                                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-ipn-900 text-white text-[10px] font-black flex items-center justify-center shadow"
                                    data-testid={`serve-indicator-${color}`}
                                    aria-label="Jugador sacando"
                                >
                                    🏐
                                </span>
                            )}
                            <span className={`text-[9px] uppercase font-bold ${isServer ? "text-ipn-900" : "text-slate-400"}`}>
                                P{idx + 1}
                            </span>
                            <span className={`text-2xl font-display font-black ${isServer ? "text-ipn-950" : ""}`}>
                                #{s.current_num}
                            </span>
                            {s.paired_with && (
                                <span className="text-[9px] text-slate-400 font-bold">
                                    ↔ #{s.paired_with}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            <Button
                onClick={onAddPoint}
                className={`mt-3 w-full h-12 rounded-xl text-base font-bold ${
                    color === "ipn"
                        ? "bg-ipn-800 hover:bg-ipn-900"
                        : "bg-slate-800 hover:bg-slate-900"
                }`}
                data-testid={`sim-point-${color}`}
            >
                +1 Punto
            </Button>
            <button
                type="button"
                onClick={onSancion}
                className="mt-2 w-full h-10 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 font-bold inline-flex items-center justify-center gap-2 text-sm transition-colors"
                data-testid={`sim-sancion-${color}`}
            >
                <ShieldAlert className="w-4 h-4" />
                Sanciones
            </button>
        </div>
    );
}

function SubstitutionDialog({ open, team, pos, slot, forced, onClose, onConfirm }) {
    const [num, setNum] = useState("");

    return (
        <Dialog
            open={open}
            onOpenChange={(o) => {
                if (!o && forced) {
                    toast.warning(
                        "Cambio forzado: ingresa el número del reemplazo.",
                    );
                    return;
                }
                if (!o) onClose();
            }}
        >
            <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display">
                        {forced ? "Cambio forzado" : "Sustitución"} · {team} · P{pos}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    {forced && (
                        <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs font-bold text-rose-700">
                            El jugador #{slot.current_num} fue
                            expulsado/descalificado. Debes ingresar un
                            reemplazo antes de continuar.
                        </div>
                    )}
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm">
                        <span className="text-xs uppercase font-bold text-slate-500">
                            Sale:
                        </span>{" "}
                        <span className="font-bold">#{slot.current_num}</span>
                    </div>

                    {slot.paired_with && !forced && (
                        <button
                            onClick={() => onConfirm(slot.paired_with)}
                            className="w-full p-4 rounded-xl border-2 border-ipn-800 bg-ipn-50 hover:bg-ipn-100 transition-all text-left"
                            data-testid="sub-pair"
                        >
                            <div className="text-xs uppercase font-bold tracking-widest text-ipn-800">
                                Volver a entrar
                            </div>
                            <div className="text-3xl font-display font-black text-ipn-900 mt-1">
                                #{slot.paired_with}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                Toca para confirmar el cambio reversible.
                            </div>
                        </button>
                    )}

                    <div>
                        <Label className="text-xs font-bold uppercase">
                            {slot.paired_with && !forced
                                ? "O escribe otro número"
                                : "Número que entra"}
                        </Label>
                        <div className="flex gap-2 mt-2">
                            <Input
                                inputMode="numeric"
                                value={num}
                                onChange={(e) => {
                                    if (
                                        e.target.value === "" ||
                                        /^\d{0,3}$/.test(e.target.value)
                                    )
                                        setNum(e.target.value);
                                }}
                                placeholder="#"
                                className="h-12 rounded-xl text-center text-xl font-bold"
                                autoFocus
                                data-testid="sub-manual-input"
                            />
                            <Button
                                onClick={() => onConfirm(num)}
                                disabled={!num.trim()}
                                className="h-12 rounded-xl bg-ipn-800 hover:bg-ipn-900 font-bold px-6"
                                data-testid="sub-manual-confirm"
                            >
                                Confirmar
                            </Button>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    {!forced && (
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="rounded-xl"
                        >
                            Cancelar
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SummaryStat({ label, v }) {
    return (
        <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
            <div className="text-[10px] uppercase font-bold tracking-widest text-slate-400">
                {label}
            </div>
            <div className="mt-1 font-display text-2xl font-black text-ipn-900">
                {v}
            </div>
        </div>
    );
}

function OfficialField({ label, value, onChange, required, testId }) {
    return (
        <div>
            <Label className="text-xs uppercase font-bold tracking-widest text-slate-500">
                {label}
                {required && <span className="text-rose-600 ml-1">*</span>}
            </Label>
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Nombre completo"
                className="mt-1 h-10 rounded-xl"
                data-testid={testId}
            />
        </div>
    );
}

function SancionDialog({ open, team, teamName, onClose, onConfirm }) {
    const [step, setStep] = useState("type"); // type | detail
    const [type, setType] = useState(null);
    const [target, setTarget] = useState("jugador");
    const [number, setNumber] = useState("");
    const [reason, setReason] = useState("");

    const reset = () => {
        setStep("type");
        setType(null);
        setTarget("jugador");
        setNumber("");
        setReason("");
    };

    const close = () => {
        reset();
        onClose();
    };

    const pick = (t) => {
        setType(t);
        setStep("detail");
    };

    const confirm = () => {
        if (target === "jugador" && !number.trim()) {
            toast.error("Indica el número del jugador.");
            return;
        }
        onConfirm({ type, target, number, reason });
        reset();
    };

    const meta = SANCION_META[type] || {};

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <ShieldAlert className="w-5 h-5 text-amber-600" />
                        Sancionar · {teamName || team}
                    </DialogTitle>
                </DialogHeader>

                {step === "type" && (
                    <div className="space-y-2">
                        <SancionOption
                            icon={<AlertTriangle className="w-5 h-5 text-yellow-600" />}
                            title="Amonestación"
                            desc="Advertencia verbal/tarjeta amarilla. No afecta el marcador."
                            onClick={() => pick("amonestacion")}
                            testId="sancion-amonestacion"
                        />
                        <SancionOption
                            icon={<Ban className="w-5 h-5 text-orange-600" />}
                            title="Castigo"
                            desc="+1 punto y saque/rotación al equipo contrario."
                            onClick={() => pick("castigo")}
                            testId="sancion-castigo"
                        />
                        <SancionOption
                            icon={<XCircle className="w-5 h-5 text-rose-600" />}
                            title="Expulsión"
                            desc="Bloquea al jugador del set actual. Cambio obligatorio si está en cancha."
                            onClick={() => pick("expulsion")}
                            testId="sancion-expulsion"
                        />
                        <SancionOption
                            icon={<X className="w-5 h-5 text-rose-900" />}
                            title="Descalificación"
                            desc="Bloquea al jugador de todo el partido. Cambio obligatorio."
                            onClick={() => pick("descalificacion")}
                            testId="sancion-descalificacion"
                        />
                    </div>
                )}

                {step === "detail" && (
                    <div className="space-y-3">
                        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
                            {meta.icon}
                            <div>
                                <div className="font-bold text-sm">
                                    {meta.title}
                                </div>
                                <div className="text-xs text-slate-600">
                                    {meta.desc}
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs uppercase font-bold tracking-widest text-slate-500">
                                ¿A quién?
                            </Label>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                <button
                                    type="button"
                                    onClick={() => setTarget("jugador")}
                                    data-testid="sancion-target-jugador"
                                    className={`h-11 rounded-xl border font-bold text-sm transition-colors ${
                                        target === "jugador"
                                            ? "bg-ipn-800 text-white border-ipn-800"
                                            : "bg-white text-slate-700 border-slate-200"
                                    }`}
                                >
                                    Jugador
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setTarget("entrenador")}
                                    data-testid="sancion-target-entrenador"
                                    className={`h-11 rounded-xl border font-bold text-sm transition-colors ${
                                        target === "entrenador"
                                            ? "bg-ipn-800 text-white border-ipn-800"
                                            : "bg-white text-slate-700 border-slate-200"
                                    }`}
                                >
                                    Entrenador
                                </button>
                            </div>
                        </div>

                        {target === "jugador" && (
                            <div>
                                <Label className="text-xs uppercase font-bold tracking-widest text-slate-500">
                                    Número del jugador
                                </Label>
                                <Input
                                    inputMode="numeric"
                                    value={number}
                                    onChange={(e) => {
                                        if (
                                            e.target.value === "" ||
                                            /^\d{0,3}$/.test(e.target.value)
                                        )
                                            setNumber(e.target.value);
                                    }}
                                    placeholder="#"
                                    className="mt-1 h-11 rounded-xl text-center text-lg font-bold"
                                    autoFocus
                                    data-testid="sancion-number"
                                />
                            </div>
                        )}

                        <div>
                            <Label className="text-xs uppercase font-bold tracking-widest text-slate-500">
                                Motivo (opcional)
                            </Label>
                            <Textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Falta, conducta, retraso de juego…"
                                className="mt-1 rounded-xl"
                                rows={2}
                                data-testid="sancion-reason"
                            />
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {step === "detail" && (
                        <Button
                            variant="outline"
                            onClick={() => setStep("type")}
                            className="rounded-xl"
                        >
                            ← Cambiar tipo
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={close}
                        className="rounded-xl"
                    >
                        Cancelar
                    </Button>
                    {step === "detail" && (
                        <Button
                            onClick={confirm}
                            className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold"
                            data-testid="sancion-confirm"
                        >
                            Aplicar sanción
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const SANCION_META = {
    amonestacion: {
        title: "Amonestación",
        desc: "Advertencia / tarjeta amarilla. Solo se registra.",
        icon: <AlertTriangle className="w-5 h-5 text-yellow-600" />,
    },
    castigo: {
        title: "Castigo",
        desc: "+1 punto y saque/rotación al equipo contrario.",
        icon: <Ban className="w-5 h-5 text-orange-600" />,
    },
    expulsion: {
        title: "Expulsión",
        desc: "Bloquea al jugador del set actual y fuerza cambio.",
        icon: <XCircle className="w-5 h-5 text-rose-600" />,
    },
    descalificacion: {
        title: "Descalificación",
        desc: "Bloquea al jugador de todo el partido.",
        icon: <X className="w-5 h-5 text-rose-900" />,
    },
};

function SancionOption({ icon, title, desc, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors flex items-start gap-3"
        >
            <div className="mt-0.5">{icon}</div>
            <div className="min-w-0">
                <div className="font-bold text-sm">{title}</div>
                <div className="text-xs text-slate-500">{desc}</div>
            </div>
        </button>
    );
}

function TimeoutOverlay({ seconds, teamName, onClose }) {
    const safe = Math.max(0, seconds);
    return (
        <div
            className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-6"
            data-testid="timeout-overlay"
        >
            <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
                <Timer className="w-10 h-10 mx-auto text-ipn-800" />
                <h3 className="mt-3 font-display text-xl font-black">
                    Tiempo fuera
                </h3>
                <p className="text-sm text-slate-500">
                    Solicitado por <strong>{teamName}</strong>
                </p>
                <div
                    className={`mt-6 font-display font-black tabular-nums ${
                        safe <= 5 ? "text-rose-600" : "text-ipn-900"
                    }`}
                    style={{ fontSize: "5rem", lineHeight: 1 }}
                    data-testid="timeout-seconds"
                >
                    {String(Math.floor(safe / 60)).padStart(1, "0")}:
                    {String(safe % 60).padStart(2, "0")}
                </div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-2">
                    30s reglamentarios
                </p>
                <Button
                    onClick={onClose}
                    className="mt-6 w-full h-12 rounded-xl bg-ipn-800 hover:bg-ipn-900 font-bold"
                    data-testid="timeout-resume"
                >
                    {safe <= 0 ? "Reanudar juego" : "Terminar antes"}
                </Button>
            </div>
        </div>
    );
}
